import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type AnimationEndCheck = (event: TransitionEvent | AnimationEvent) => boolean;

interface UseDialogLifecycleOptions {
  /** Controls whether the dialog is open */
  isOpen: boolean;
  /** Callback invoked when close is requested (Escape key, close button, click outside) */
  onClose?: () => void;
  /** Callback invoked after the close animation completes */
  onClosed?: () => void;
  /** Optional callback invoked before closing on Escape/cancel. Use for custom escape handling (e.g., focusing elements for blur events) */
  onEscape?: (event: Event) => void;
  /** Type of animation used for closing ('transition' for CSS transitions, 'animation' for CSS animations) */
  animationType: 'transition' | 'animation';
  /** Function to check if the animation/transition that ended is the one we're waiting for */
  animationEndCheck: AnimationEndCheck;
  /** Disable native dialog cancel event handling (when Escape is handled elsewhere, e.g., via FocusOn) */
  disableNativeCancel?: boolean;
}

interface UseDialogLifecycleReturn {
  /** Ref to attach to the <dialog> element */
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  /** Whether the dialog should be rendered in the DOM */
  shouldRender: boolean;
  /** Handler to call when user requests close (for FocusOn callbacks, escape key, etc.) */
  handleClose: () => void;
}

/**
 * Custom hook that manages the lifecycle of a modal dialog with animations.
 * Handles opening, closing with animations, escape key, and cleanup.
 *
 * @example
 * ```tsx
 * const { dialogRef, shouldRender, handleClose } = useDialogLifecycle({
 *   isOpen,
 *   onClose: () => setIsOpen(false),
 *   onClosed: () => console.log('Animation complete'),
 *   animationType: 'transition',
 *   animationEndCheck: (e) => e.propertyName === 'transform',
 * });
 *
 * return shouldRender ? (
 *   <dialog ref={dialogRef}>
 *     <FocusOn onEscapeKey={handleClose}>...</FocusOn>
 *   </dialog>
 * ) : null;
 * ```
 */
export function useDialogLifecycle({
  isOpen,
  onClose,
  onClosed,
  onEscape,
  animationType,
  animationEndCheck,
  disableNativeCancel = false,
}: UseDialogLifecycleOptions): UseDialogLifecycleReturn {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const animationControllerRef = useRef<AbortController | null>(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [internalIsOpen, setInternalIsOpen] = useState(isOpen);

  // Sync with external isOpen prop
  useEffect(() => {
    if (isOpen) {
      // Abort any pending close animation when reopening
      if (animationControllerRef.current) {
        animationControllerRef.current.abort();
        animationControllerRef.current = null;
      }
      // Remove closing attribute if it exists
      if (dialogRef.current?.hasAttribute('closing')) {
        dialogRef.current.removeAttribute('closing');
      }
      setShouldRender(true);
      setInternalIsOpen(true);
    } else {
      setInternalIsOpen(false);
    }
  }, [isOpen]);

  // Hide function - handles the closing animation
  const hide = useCallback(() => {
    if (dialogRef.current) {
      const modal = dialogRef.current;
      const controller = new AbortController();
      animationControllerRef.current = controller;

      modal.setAttribute('closing', '');
      const eventType = animationType === 'transition' ? 'transitionend' : 'animationend';

      modal.addEventListener(
        eventType,
        (event) => {
          // Only handle events on the dialog itself, not bubbled events from children
          if (event.target !== modal) {
            return;
          }
          // Ignore events from pseudo-elements (e.g., ::backdrop)
          // The ::backdrop also animates and fires animationend events that bubble to the dialog
          if ((event as AnimationEvent | TransitionEvent).pseudoElement) {
            return;
          }
          if (animationEndCheck(event)) {
            modal.removeAttribute('closing');
            modal.close();
            animationControllerRef.current = null;
            setShouldRender(false);
            onClosed?.();
          }
          // Other animations/transitions might be running in parallel, so 'once: true' is not sufficient
        },
        { signal: controller.signal },
      );
    }
  }, [animationType, animationEndCheck, onClosed]);

  // Close handler - called when user requests close
  const handleClose = useCallback(() => {
    onClose?.();
    setInternalIsOpen(false);
  }, [onClose]);

  // Always prevent the native dialog cancel to avoid state conflicts.
  // When disableNativeCancel is true, Escape is handled via FocusOn's onEscapeKey instead.
  useEffect(() => {
    const modal = dialogRef.current;
    if (!modal) {
      return;
    }

    const handleCancel = (event: Event) => {
      event.preventDefault();
      if (!disableNativeCancel) {
        onEscape?.(event);
        handleClose();
      }
    };

    modal.addEventListener('cancel', handleCancel);
    return () => modal.removeEventListener('cancel', handleCancel);
    // shouldRender is needed so the listener re-registers when the dialog element is recreated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, onEscape, disableNativeCancel, shouldRender]);

  // Dialog element lifecycle - showModal() when opening, hide() when closing
  useEffect(() => {
    const modal = dialogRef.current;
    if (!modal) {
      return;
    }
    if (shouldRender && !modal.hasAttribute('open')) {
      modal.showModal();
    }
    if (!internalIsOpen && modal.hasAttribute('open') && !modal.hasAttribute('closing')) {
      hide();
    }
  }, [hide, internalIsOpen, shouldRender]);

  // Cleanup animation controller on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (animationControllerRef.current) {
        animationControllerRef.current.abort();
        animationControllerRef.current = null;
      }
    };
  }, []);

  return {
    dialogRef,
    shouldRender,
    handleClose,
  };
}
